import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { LOG_MODULE, LoggerService, extractErrorCode } from '../../core/logging';
import { AuthService } from './auth.service';
import { AuthStateService } from './auth-state.service';
import { CsrfTokenService } from './csrf.service';
import { ReturnUrlService } from './return-url.service';

export type SessionExpiryReason =
  | 'session-expired'
  | 'session-max-age'
  | 'session-invalid'
  | 'session-replaced';

export const SESSION_EXPIRY_REASONS: readonly SessionExpiryReason[] = [
  'session-expired',
  'session-max-age',
  'session-invalid',
  'session-replaced',
];

const LOGIN_REDIRECT_PATH = '/';
const AUTH_ROUTE_PREFIXES: readonly string[] = ['/auth'];
const MAX_AGE_ERROR_CODE = 'SESSION_EXPIRED';
const REPLACED_ERROR_CODE = 'SESSION_REVOKED';
const INVALID_ERROR_CODES: readonly string[] = ['SESSION_USER_AGENT_MISMATCH'];

export function isSessionExpiryReason(value: string | null | undefined): value is SessionExpiryReason {
  return SESSION_EXPIRY_REASONS.some((reason) => reason === value);
}

@Injectable({ providedIn: 'root' })
export class SessionExpiryService {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly authState = inject(AuthStateService);
  private readonly csrfTokenService = inject(CsrfTokenService);
  private readonly returnUrl = inject(ReturnUrlService);
  private readonly logger = inject(LoggerService);

  public reasonFor(error: unknown): SessionExpiryReason {
    const code = extractErrorCode(error);

    if (code === MAX_AGE_ERROR_CODE) {
      return 'session-max-age';
    }
    if (code === REPLACED_ERROR_CODE) {
      return 'session-replaced';
    }
    if (code !== undefined && INVALID_ERROR_CODES.includes(code)) {
      return 'session-invalid';
    }

    return 'session-expired';
  }

  public handle(error: unknown, source: string): void {
    this.end(this.reasonFor(error), source, error);
  }

  public end(reason: SessionExpiryReason, source: string, error?: unknown): void {
    this.authService.isAuthenticatedSignal.set(false);
    this.authState.reset();
    this.csrfTokenService.clearToken();

    const onAuthPage = this.isOnAuthPage();

    this.logger.warn('Session ended, user signed out', error, {
      module: LOG_MODULE.sessionExpired,
      source,
      reason,
      redirected: !onAuthPage,
    });

    if (onAuthPage) {
      return;
    }
    this.returnUrl.remember(this.router.url);
    void this.router.navigate([LOGIN_REDIRECT_PATH], { queryParams: { reason } });
  }

  private isOnAuthPage(): boolean {
    const currentUrl = this.router.url.split('?')[0];

    return (
      currentUrl === LOGIN_REDIRECT_PATH ||
      currentUrl === '' ||
      AUTH_ROUTE_PREFIXES.some((prefix) => currentUrl.startsWith(prefix))
    );
  }
}
